import { canInAnyScope } from './permissions.js';
import type { Notification } from './schemas/notification.js';
import type { AuthenticatedUser } from './schemas/user.js';

// #76/I1: EINE Regel fuer den Deeplink eines Benachrichtigungs-Ereignisses — Client (Glocke,
// notification-bell.tsx) und Server (Mail-Dispatch, dispatch.ts) muessen denselben Pfad waehlen.
// Vorher kannte nur der Client die Rechtepruefung, die Mail verlinkte immer `/defects/:id` — fuer
// einen Empfaenger ohne `defect.edit` (z. B. Tester) eine Route, die seine Shell nicht kennt; er
// landete wortlos auf "Meine Tests". Deshalb liegt die Regel hier zentral statt zweimal.
export function deeplinkForNotification(
  n: Pick<Notification, 'kind' | 'payload'>,
  user: AuthenticatedUser | null,
): string {
  if (n.kind === 'defect_assigned' && user && canInAnyScope(user, 'defect.edit')) {
    const defectId = typeof n.payload.defect_id === 'string' ? n.payload.defect_id : '';
    return `/defects/${defectId}`;
  }
  // #92: retest_due traegt den betroffenen Testfall im Payload (defects.ts, retest_cascaded) —
  // `?case=` waehlt ihn auf `/my-tests` vor (my-tests-page.tsx, Muster wie Katalog `?test_case=`).
  // Kein Rechte-Gate noetig: `/my-tests` kennt jede Rolle, die diese Benachrichtigung ueberhaupt
  // bekommt (NOT-3 adressiert den Tester des Testfalls bzw. ersatzweise den Defect-Assignee).
  // #194: retest_due mit `fix_check` ist die Bitte an den Melder, die Behebung zu bestätigen — der
  // Deeplink öffnet die Karte „Fehler prüfen" (`?fixcheck=<defect_id>`, my-tests-page.tsx) statt
  // eines Testfalls. Ohne defect_id gibt es nichts zu öffnen: dann gilt das bisherige Verhalten.
  if (n.kind === 'retest_due' && n.payload.fix_check === true) {
    const defectId = typeof n.payload.defect_id === 'string' ? n.payload.defect_id : '';
    if (defectId) return `/my-tests?fixcheck=${defectId}`;
  }
  if (n.kind === 'retest_due') {
    const testCaseId = typeof n.payload.test_case_id === 'string' ? n.payload.test_case_id : '';
    return testCaseId ? `/my-tests?case=${testCaseId}` : '/my-tests';
  }
  // #194: fix_rejected geht an den Bearbeiter des Fehlers. Wer `defect.edit` hat, kennt `/defects/:id`
  // (wie defect_assigned, s. o.); sonst landet er auf der Liste, weil seine Shell die Route nicht kennt.
  if (n.kind === 'fix_rejected' && user && canInAnyScope(user, 'defect.edit')) {
    const defectId = typeof n.payload.defect_id === 'string' ? n.payload.defect_id : '';
    return `/defects/${defectId}`;
  }
  // #195: handover_received / handover_taken landen bewusst auf der nackten Liste — die Weitergabe
  // betrifft mehrere Testfälle (Payload `count`), kein einzelner passt zu `?case=`. Der Fall-Through
  // liefert das schon; der Test in notification-deeplink.test.ts hält es fest.
  return '/my-tests';
}
